import React, { useState } from 'react';
import { Card, Table, Badge, Nav, Tab, Alert, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileExcel, 
  faTable, 
  faColumns, 
  faDatabase,
  faCheckCircle,
  faExclamationTriangle,
  faEye
} from '@fortawesome/free-solid-svg-icons';

interface FileStructurePreviewProps {
  fileStructure: {
    filename: string;
    sheets: Array<{
      name: string;
      columns: string[];
      sampleData: any[];
      rowCount: number;
    }>;
  };
  onSheetSelect: (sheetName: string) => void;
}

const FileStructurePreview: React.FC<FileStructurePreviewProps> = ({
  fileStructure,
  onSheetSelect
}) => {
  const [selectedSheet, setSelectedSheet] = useState(fileStructure.sheets[0]?.name || '');
  const [showFullPreview, setShowFullPreview] = useState(false);

  const handleSheetChange = (sheetName: string) => {
    setSelectedSheet(sheetName);
    onSheetSelect(sheetName);
  };

  const getDataTypeIcon = (value: any) => {
    if (value === null || value === undefined || value === '') {
      return { icon: faExclamationTriangle, color: 'text-warning', type: 'Empty' };
    }
    if (!isNaN(value) && !isNaN(parseFloat(value))) {
      return { icon: faDatabase, color: 'text-info', type: 'Number' };
    }
    if (value.includes('@')) {
      return { icon: faCheckCircle, color: 'text-success', type: 'Email' };
    }
    if (value.match(/^\d{3}-\d{3}-\d{4}$/) || value.match(/^\(\d{3}\) \d{3}-\d{4}$/)) {
      return { icon: faCheckCircle, color: 'text-success', type: 'Phone' };
    }
    return { icon: faTable, color: 'text-primary', type: 'Text' };
  };

  const currentSheet = fileStructure.sheets.find(s => s.name === selectedSheet);
  const previewRows = showFullPreview ? currentSheet?.sampleData : currentSheet?.sampleData.slice(0, 5);

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-gradient-primary text-white">
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={faFileExcel} className="me-2" />
          <span>File Preview: {fileStructure.filename}</span>
          {fileStructure.sheets.length > 1 && (
            <Badge bg="light" text="dark" className="ms-auto">
              {fileStructure.sheets.length} sheets
            </Badge>
          )}
        </div>
      </Card.Header>
      
      <Card.Body>
        {fileStructure.sheets.length > 1 && (
          <Tab.Container activeKey={selectedSheet} onSelect={(k) => handleSheetChange(k || '')}>
            <Nav variant="tabs" className="mb-3">
              {fileStructure.sheets.map(sheet => (
                <Nav.Item key={sheet.name}>
                  <Nav.Link eventKey={sheet.name}>
                    <FontAwesomeIcon icon={faTable} className="me-2" />
                    {sheet.name}
                    <Badge bg="secondary" className="ms-2">{sheet.rowCount} rows</Badge>
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </Tab.Container>
        )}

        {currentSheet && (
          <>
            {/* Column Summary */}
            <Alert variant="info" className="mb-3">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faColumns} className="me-2" />
                <strong>{currentSheet.columns.length} columns detected</strong>
                <span className="ms-auto">
                  <FontAwesomeIcon icon={faDatabase} className="me-2" />
                  {currentSheet.rowCount} total records
                </span>
              </div>
            </Alert>

            {/* Column List */}
            <div className="mb-4">
              <h6 className="mb-3">Detected Columns:</h6>
              <div className="d-flex flex-wrap gap-2">
                {currentSheet.columns.map((col, idx) => (
                  <Badge key={idx} bg="secondary" className="p-2">
                    {col}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Data Preview */}
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Sample Data:</h6>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => setShowFullPreview(!showFullPreview)}
                >
                  <FontAwesomeIcon icon={faEye} className="me-2" />
                  {showFullPreview ? 'Show Less' : 'Show More'}
                </Button>
              </div>
              
              <div className="table-responsive">
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>#</th>
                      {currentSheet.columns.map((col, idx) => (
                        <th key={idx}>
                          {col}
                          <div className="small text-muted mt-1">
                            {currentSheet.sampleData.length > 0 && (
                              <span className={getDataTypeIcon(currentSheet.sampleData[0][col]).color}>
                                {getDataTypeIcon(currentSheet.sampleData[0][col]).type}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows?.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        <td>{rowIdx + 1}</td>
                        {currentSheet.columns.map((col, colIdx) => {
                          const value = row[col];
                          const dataType = getDataTypeIcon(value);
                          return (
                            <td key={colIdx}>
                              {value || (
                                <span className="text-muted font-italic">empty</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {currentSheet.sampleData.length > 5 && !showFullPreview && (
                <div className="text-center text-muted small mt-2">
                  Showing {Math.min(5, currentSheet.sampleData.length)} of {currentSheet.sampleData.length} sample rows
                </div>
              )}
            </div>

            {/* Data Quality Indicators */}
            <Alert variant="success" className="mt-3 mb-0">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              File structure analyzed successfully. Ready for field mapping.
            </Alert>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default FileStructurePreview;